import { useEffect, useMemo, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import {
  listFornecedores,
  TIPO_FORNECEDOR_OPTIONS,
  tipoFornecedorIcon,
  tipoFornecedorLabel,
} from "../services/fornecedoresService";
import Loading, { ErrorBox } from "../components/ui/Loading";
import FornecedorFormModal from "../components/forms/FornecedorFormModal";

export default function FornecedoresPage() {
  const { C } = useTheme();
  const [fornecedores, setFornecedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [tipoFiltro, setTipoFiltro] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  async function reload() {
    setLoading(true);
    try {
      const res = await listFornecedores();
      setFornecedores(res);
      setError(null);
    } catch {
      setError("Não foi possível carregar os fornecedores.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  const filtered = useMemo(() => {
    let list = fornecedores;
    if (tipoFiltro !== "") {
      const t = Number(tipoFiltro);
      list = list.filter((f) => f.tipo === t);
    }
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(
        (f) =>
          f.nome?.toLowerCase().includes(s) ||
          f.cidade?.toLowerCase().includes(s) ||
          f.email?.toLowerCase().includes(s)
      );
    }
    return list;
  }, [fornecedores, search, tipoFiltro]);

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>
            Fornecedores
          </h1>
          <p style={{ color: C.textMuted, fontSize: 13, margin: "4px 0 0" }}>
            {fornecedores.length} fornecedor{fornecedores.length === 1 ? "" : "es"} cadastrado
            {fornecedores.length === 1 ? "" : "s"}
          </p>
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
          style={{
            padding: "10px 20px",
            borderRadius: 10,
            border: "none",
            background: C.accent,
            color: "#fff",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          + Novo Fornecedor
        </button>
      </div>

      <FornecedorFormModal
        open={modalOpen}
        fornecedor={editing}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSaved={reload}
        onDeleted={reload}
      />

      <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar fornecedor..."
          style={{
            flex: 1,
            minWidth: 220,
            maxWidth: 360,
            padding: "10px 16px",
            borderRadius: 10,
            border: `1px solid ${C.border}`,
            background: C.inputBg,
            color: C.text,
            fontSize: 13,
            outline: "none",
            fontFamily: "'DM Sans', sans-serif",
            boxSizing: "border-box",
          }}
        />
        <select
          value={tipoFiltro}
          onChange={(e) => setTipoFiltro(e.target.value)}
          style={{
            padding: "10px 16px",
            borderRadius: 10,
            border: `1px solid ${C.border}`,
            background: C.inputBg,
            color: C.text,
            fontSize: 13,
            outline: "none",
            fontFamily: "'DM Sans', sans-serif",
            minWidth: 180,
          }}
        >
          <option value="">Todos os tipos</option>
          {TIPO_FORNECEDOR_OPTIONS.map((t) => (
            <option key={t.value} value={t.value}>
              {t.icon} {t.label}
            </option>
          ))}
        </select>
      </div>

      {loading && <Loading />}
      {error && <ErrorBox message={error} />}

      {!loading && !error && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.map((f) => (
            <div
              key={f.id}
              onClick={() => {
                setEditing(f);
                setModalOpen(true);
              }}
              style={{
                background: C.bgCard,
                borderRadius: 12,
                padding: "16px 20px",
                border: `1px solid ${C.border}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                gap: 16,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 10,
                    background: C.accentDim,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18,
                    flexShrink: 0,
                  }}
                >
                  {tipoFornecedorIcon(f.tipo)}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: C.text,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {f.nome}
                  </div>
                  <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>
                    {tipoFornecedorLabel(f.tipo)}
                    {f.cidade ? ` · ${f.cidade}` : ""}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ textAlign: "right" }}>
                  {f.telefone && (
                    <div style={{ fontSize: 11, color: C.textMuted }}>{f.telefone}</div>
                  )}
                  {f.email && (
                    <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>{f.email}</div>
                  )}
                </div>
                <span style={{ color: C.textDim, fontSize: 16 }}>›</span>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ fontSize: 13, color: C.textMuted, padding: 20, textAlign: "center" }}>
              {fornecedores.length === 0
                ? "Nenhum fornecedor cadastrado ainda — comece adicionando o primeiro hotel ou transportadora."
                : "Nenhum fornecedor encontrado com esses filtros."}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
